import type { BoardDto, BoardItemDto } from '@nsb/core';
import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type Ref } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, api, errorText, queryClient, upload, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { FULL_SCREEN, useFullScreen } from '../../components/fullScreen.ts';
import { NO_SCROLLBARS, usePaper } from '../../components/paper.ts';
import { useWide } from '../../components/useWide.ts';
import { Button, ErrorLine, LoadError, Loading, Page, TextInput, Title } from '../../components/ui.tsx';
import { SCRAP_FOOT, isPageAddress, scrollOnto, siteOf, tiltOf } from './boardLook.ts';
import { ProjectCrumb } from './common.tsx';
import { assetsKey, useProject, useProjectId } from './hooks.ts';
import { typeOf } from '../../fileTypes.ts';
import { dateWords } from '../../days.ts';

/**
 * The design board (process-flow spec: "a standing, open-ended creative space,
 * always available, never nagging"; Jason, 2026-10-03: "visible to all
 * parties ... ctrl v or command v paste images, drag images"). One free
 * canvas both sides put pictures, pins, links and notes on and arrange by
 * hand. A picture opens on its own page to be marked up and talked about. On
 * a phone the board reads top to bottom, as it is laid out; arranging it waits
 * for a wider screen. On a wide one it goes full screen, the whole window and
 * no frame (Jason, 2026-10-04: "the design board also should be able to go
 * full screen and not be bound by a frame"): its paper runs on every way,
 * dragged to move round it, the wheel zooming, as every drawing does.
 */

const boardKey = (projectId: string) => ['project', projectId, 'board'];
const ZOOMS = [0.25, 0.4, 0.6, 0.8, 1, 1.25];
/**
 * How far the paper runs up and to the left of where the board starts: as far
 * as a thing can be put there (the API's -1000), and a little paper beyond.
 * A multiple of the dots' spacing, so the dots run on unbroken.
 */
const ORIGIN = 1024;
const LEAST = -1000;
const day = (iso: string) => dateWords(iso, 'date');
const host = (url: string | null) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : '';
  } catch {
    return '';
  }
};
const isWeb = (s: string | null | undefined): boolean => !!s && /^https?:\/\/\S+$/i.test(s.trim());

type Point = { x: number; y: number };

/** What the canvas tells the page: where the middle of its view is, for something pasted or added. */
interface CanvasHandle {
  center: () => Point;
}

/** Under everything on the board: where something lands when there is no view to put it in the middle of. */
const below = (items: BoardItemDto[]): Point => ({ x: 40, y: Math.max(40, ...items.map((i) => i.y + heightOf(i) + 40)) });

/** How tall an item stands on the board, in board units: a picture brought from a page stands on its scrap, its title and site under it. */
function heightOf(it: BoardItemDto): number {
  if (it.kind === 'image' && it.asset?.width && it.asset.height) {
    const picture = (it.w * it.asset.height) / it.asset.width;
    return isPageAddress(it.asset.sourceUrl) ? picture + SCRAP_FOOT : picture;
  }
  return it.kind === 'note' ? 72 : 84;
}

/** A cut sheet lying on the board (design study 05, 3A). */
const SCRAP = 'border border-[rgba(170,166,156,0.45)] bg-scrap text-ink shadow-[0_1px_1px_rgba(32,35,31,0.08),0_6px_14px_rgba(32,35,31,0.1)]';

/** The thing on the board a link is to (`?item=`, from a line of the timeline, P-26), while it is still on it. */
function useWanted(items: BoardItemDto[]): BoardItemDto | null {
  const [search] = useSearchParams();
  const id = search.get('item');
  return (id && items.find((i) => i.id === id)) || null;
}

/** Putting things on the board, however they arrive: picked, pasted, dropped, linked or written. */
function usePosting(projectId: string) {
  const [sending, setSending] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const land = (b: BoardDto) => {
    queryClient.setQueryData(boardKey(projectId), b);
    void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
  };
  const run = async (what: string, fn: () => Promise<BoardDto>) => {
    setErr(null);
    setSending(what);
    try {
      land(await fn());
      return true;
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 413 ? 'That picture is too big for the board (30 MB).' : errorText(e));
      return false;
    } finally {
      setSending(null);
    }
  };
  const pictures = async (files: File[], at: Point, meta: { caption?: string | null; source?: string | null } = {}) => {
    for (const [i, file] of files.entries()) {
      const type = typeOf(file);
      // Sent as it comes: a pasted picture's "image.png", or any name only an id, is named by the API's one rule (its
      // caption, else "Design board, picture n": core `nameAsItComes`).
      const name = file.name.trim() || 'image';
      const q = new URLSearchParams({ name, x: String(Math.round(at.x + i * 28)), y: String(Math.round(at.y + i * 28)) });
      if (meta.caption) q.set('caption', meta.caption.slice(0, 500));
      if (meta.source && isWeb(meta.source)) q.set('source', meta.source.trim());
      const ok = await run(files.length > 1 ? `Adding ${i + 1} of ${files.length}` : 'Adding the picture', () =>
        upload<BoardDto>(`/projects/${projectId}/board/pictures?${q.toString()}`, file, type),
      );
      if (!ok) break;
    }
  };
  const link = (url: string, at: Point) => run('Bringing in the link', () => api<BoardDto>(`/projects/${projectId}/board/links`, { body: { url: url.trim(), x: Math.round(at.x), y: Math.round(at.y) } }));
  const note = (text: string, at: Point) => run('Adding the note', () => api<BoardDto>(`/projects/${projectId}/board/notes`, { body: { text, x: Math.round(at.x), y: Math.round(at.y) } }));
  /** Whatever was pasted or dropped: pictures first (captioned by their alt text), then a link (a pin's page over its thumbnail), then words as a note. */
  const transfer = async (dt: DataTransfer, at: Point) => {
    const files = [...dt.files].filter((f) => f.type.startsWith('image/') || /\.(heic|heif|avif|tiff?)$/i.test(f.name));
    const html = dt.getData('text/html');
    const img = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('img') : null;
    const src = img?.getAttribute('src') ?? null;
    const alt = img?.getAttribute('alt')?.trim() || null;
    const uri = dt
      .getData('text/uri-list')
      .split(/\r?\n/)
      .find((l) => l && !l.startsWith('#'))
      ?.trim();
    const text = dt.getData('text/plain').trim();
    if (files.length) return pictures(files, at, { caption: alt, source: uri && isWeb(uri) ? uri : src });
    if (uri && isWeb(uri)) return void (await link(uri, at));
    if (src && isWeb(src)) return void (await link(src, at));
    if (isWeb(text)) return void (await link(text, at));
    if (text) return void (await note(text.slice(0, 500), at));
  };
  return { sending, err, setErr, pictures, link, note, transfer };
}

export function Board() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<BoardDto>(boardKey(projectId), `/projects/${projectId}/board`);
  const wide = useWide();
  const [full, setFull] = useFullScreen();
  const post = usePosting(projectId);
  const canvas = useRef<CanvasHandle>(null);
  // Where a pasted or added thing lands: the middle of what the canvas shows, or under everything on a phone.
  const at = (): Point => canvas.current?.center() ?? below(queryClient.getQueryData<BoardDto>(boardKey(projectId))?.items ?? []);
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — design board` } : null);

  // Paste anywhere on the page, except into a field being typed in.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (!e.clipboardData || t?.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      void post.transfer(e.clipboardData, canvas.current?.center() ?? below(queryClient.getQueryData<BoardDto>(boardKey(projectId))?.items ?? []));
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [post, projectId]);

  // A picture or link dropped beside the canvas (or anywhere on a phone, which has none) goes on the board, never opened
  // by the browser in the app's place. The canvas takes its own drops first; a field takes the words dropped in it.
  useEffect(() => {
    const carries = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).some((t) => t === 'Files' || t === 'text/uri-list' || t === 'text/plain');
    const inField = (e: DragEvent) => Boolean((e.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable="true"]'));
    const hold = (e: DragEvent) => {
      if (carries(e) && !inField(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      if (e.defaultPrevented || !carries(e) || inField(e) || !e.dataTransfer) return;
      e.preventDefault();
      void post.transfer(e.dataTransfer, canvas.current?.center() ?? below(queryClient.getQueryData<BoardDto>(boardKey(projectId))?.items ?? []));
    };
    window.addEventListener('dragover', hold);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', hold);
      window.removeEventListener('drop', drop);
    };
  }, [post, projectId]);

  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const staff = project.viewerRole === 'contractor';
  if (full && wide) {
    // The whole window, edge to edge: the ways to add along the top, and the paper.
    return (
      <div className={`${FULL_SCREEN} flex flex-col gap-2 !px-0 !pb-0`}>
        <div className="flex flex-wrap items-center gap-x-8 px-4">
          <span className="font-display text-[1.375rem]">Design board</span>
          <Adder post={post} at={at} compact />
        </div>
        <Canvas ref={canvas} projectId={projectId} items={data.items} post={post} full onFull={setFull} />
      </div>
    );
  }
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title
        how={{
          line: staff ? 'The client’s board, and yours to add to.' : 'Collect what you are drawn to; the finishes start from here.',
          intent: staff
            ? 'The client’s space to collect what they are drawn to, and yours to add to. When the finishes come due, the choices start from here.'
            : 'Collect what you are drawn to. When it is time to choose the finishes, the choices start from here.',
          steps: [
            'Paste a picture or a link (Ctrl+V or ⌘V), or drop one on the board; from a pin, paste its link.',
            'Drag to arrange; the arrow at the bottom corner sizes it.',
            'Add a note to name a group; double-click it to change its words.',
            'Drag the paper to move round; the wheel zooms.',
          ],
        }}
      >
        Design board
      </Title>
      <Adder post={post} at={at} />
      {wide ? <Canvas ref={canvas} projectId={projectId} items={data.items} post={post} full={false} onFull={setFull} /> : <Stack projectId={projectId} items={data.items} />}
    </Page>
  );
}

function Adder({ post, at, compact = false }: { post: ReturnType<typeof usePosting>; at: () => Point; compact?: boolean }) {
  const picker = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'link' | 'note' | null>(null);
  const [value, setValue] = useState('');
  const submit = async () => {
    const v = value.trim();
    if (!v) return;
    const ok = mode === 'link' ? await post.link(v, at()) : await post.note(v, at());
    if (ok) {
      setValue('');
      setMode(null);
    }
  };
  return (
    <div className={compact ? '' : 'mt-6'}>
      <input
        ref={picker}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        className="sr-only"
        aria-label="Add pictures"
        onChange={(e) => {
          void post.pictures([...(e.target.files ?? [])], at());
          e.target.value = '';
        }}
      />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Button tone="secondary" busy={post.sending !== null} onClick={() => picker.current?.click()}>
          Add pictures
        </Button>
        <Button tone="quiet" onClick={() => setMode(mode === 'link' ? null : 'link')}>
          Add a link
        </Button>
        <Button tone="quiet" onClick={() => setMode(mode === 'note' ? null : 'note')}>
          Add a note
        </Button>
      </div>
      {mode ? (
        <form
          className="mt-3 flex max-w-2xl flex-wrap items-end gap-x-4 gap-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="min-w-0 flex-1">
            <span className="label block">{mode === 'link' ? 'A link: a Pinterest pin, a picture, a page' : 'A few words, to name a group'}</span>
            <TextInput
              autoFocus
              type={mode === 'link' ? 'url' : 'text'}
              inputMode={mode === 'link' ? 'url' : 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={mode === 'link' ? 'https://…' : undefined}
            />
          </label>
          <Button type="submit" busy={post.sending !== null} disabled={!value.trim()}>
            Put it on the board
          </Button>
        </form>
      ) : null}
      {post.sending ? <p className="meta mt-2 motion-safe:animate-pulse">{post.sending}</p> : null}
      <ErrorLine error={post.err} />
    </div>
  );
}

/**
 * The free canvas: drag to arrange, the corner to size, a note's words by
 * double-click; the paper dragged to move round it, the wheel to zoom. Board
 * units run from `LEAST` up and left to as far as things go down and right,
 * drawn `ORIGIN` in.
 */
function Canvas({
  ref,
  projectId,
  items,
  post,
  full,
  onFull,
}: {
  ref: Ref<CanvasHandle>;
  projectId: string;
  items: BoardItemDto[];
  post: ReturnType<typeof usePosting>;
  full: boolean;
  onFull: (on: boolean) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const sizer = useRef<HTMLDivElement>(null);
  const world = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  // A line of the timeline opens the board on the thing it is about, chosen (P-26).
  const wanted = useWanted(items);
  const [selected, setSelected] = useState<string | null>(wanted?.id ?? null);
  const [editing, setEditing] = useState<string | null>(null);
  const [live, setLive] = useState<{ id: string; x: number; y: number; w: number } | null>(null);
  const drag = useRef<{ id: string; mode: 'move' | 'size'; px: number; py: number; x: number; y: number; w: number; moved: boolean } | null>(null);
  const [dropping, setDropping] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const shown = items.map((it) => (live && live.id === it.id ? { ...it, x: live.x, y: live.y, w: live.w } : it));
  const width = Math.max(2400, ...shown.map((i) => i.x + i.w + 480));
  const height = Math.max(1400, ...shown.map((i) => i.y + heightOf(i) + 480));
  const sel = items.find((i) => i.id === selected) ?? null;

  const middle = (): Point => {
    const el = scroller.current;
    if (!el) return { x: 80, y: 80 };
    return { x: Math.max(LEAST, (el.scrollLeft + el.clientWidth / 2) / zoom - ORIGIN - 120), y: Math.max(LEAST, (el.scrollTop + el.clientHeight / 2) / zoom - ORIGIN - 90) };
  };
  useImperativeHandle(ref, () => ({ center: middle }));
  const toBoard = (clientX: number, clientY: number): Point => {
    const el = scroller.current!;
    const r = el.getBoundingClientRect();
    return { x: Math.max(LEAST, (clientX - r.left + el.scrollLeft) / zoom - ORIGIN), y: Math.max(LEAST, (clientY - r.top + el.scrollTop) / zoom - ORIGIN) };
  };
  // The paper is dragged to move round it and the wheel zooms, as on every drawing (components/paper.ts); a click on it
  // puts down what was chosen.
  const paper = usePaper(scroller, {
    zoom,
    setZoom,
    min: ZOOMS[0]!,
    max: ZOOMS.at(-1)!,
    isPaper: (t) => t === scroller.current || t === sizer.current || t === world.current,
    onTap: () => {
      setSelected(null);
      setEditing(null);
    },
  });
  // It opens on where the board starts, a little paper above and to the left, or on the thing a link is to; the rest of
  // the paper is a drag away.
  const opened = useRef(false);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || opened.current) return;
    opened.current = true;
    const at = wanted ? scrollOnto({ ...wanted, h: heightOf(wanted) }, { w: el.clientWidth, h: el.clientHeight }, 1, ORIGIN) : { left: ORIGIN - 24, top: ORIGIN - 24 };
    el.scrollTo({ ...at, behavior: 'instant' });
    // The thing in the middle of the board, the board in the middle of the window, so it is in sight on any screen.
    if (wanted) el.scrollIntoView({ block: 'center', behavior: 'instant' });
  }, [wanted]);

  // Right-click, Paste (Jason, 2026-10-03). A browser offers Paste only over something that takes typing, so a
  // right-click on the board slips an unseen box under the pointer first; Paste from the menu lands where it was clicked.
  const catcher = useRef<HTMLTextAreaElement>(null);
  const pasteAt = useRef<Point | null>(null);
  const put = (left: number, top: number) => {
    if (!catcher.current) return;
    catcher.current.style.left = `${left}px`;
    catcher.current.style.top = `${top}px`;
  };
  const arm = (e: React.MouseEvent) => {
    if (e.button !== 2 || (e.target as HTMLElement).closest('a, button, input, textarea, select, [contenteditable="true"]')) return;
    e.preventDefault(); // or the press takes the focus straight back off the box
    pasteAt.current = toBoard(e.clientX, e.clientY);
    put(e.clientX - 12, e.clientY - 12);
    catcher.current?.focus({ preventScroll: true });
  };
  const release = () => {
    pasteAt.current = null;
    put(-9999, 0);
    if (document.activeElement === catcher.current) catcher.current?.blur();
  };
  const save = async (id: string, body: Record<string, unknown>) => {
    setErr(null);
    try {
      queryClient.setQueryData(boardKey(projectId), await api<BoardDto>(`/projects/${projectId}/board/items/${id}`, { method: 'PATCH', body }));
    } catch (e) {
      setErr(errorText(e));
      void queryClient.invalidateQueries({ queryKey: boardKey(projectId) });
    } finally {
      setLive(null);
    }
  };
  const remove = async (id: string) => {
    setErr(null);
    try {
      queryClient.setQueryData(boardKey(projectId), await api<BoardDto>(`/projects/${projectId}/board/items/${id}`, { method: 'DELETE' }));
      void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
      setSelected(null);
    } catch (e) {
      setErr(errorText(e));
    }
  };

  const begin = (e: React.PointerEvent, it: BoardItemDto, mode: 'move' | 'size') => {
    if (editing === it.id || e.button !== 0) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { id: it.id, mode, px: e.clientX, py: e.clientY, x: it.x, y: it.y, w: it.w, moved: false };
  };
  const follow = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / zoom;
    const dy = (e.clientY - d.py) / zoom;
    if (!d.moved && Math.hypot(dx, dy) * zoom < 3) return;
    d.moved = true;
    setLive(d.mode === 'move' ? { id: d.id, x: Math.max(LEAST, Math.round(d.x + dx)), y: Math.max(LEAST, Math.round(d.y + dy)), w: d.w } : { id: d.id, x: d.x, y: d.y, w: Math.min(1600, Math.max(60, Math.round(d.w + dx))) });
  };
  const end = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved || !live) {
      setSelected(d.id);
      setLive(null);
      return;
    }
    setSelected(d.id);
    void save(d.id, d.mode === 'move' ? { x: live.x, y: live.y } : { w: live.w });
  };
  const nudge = (e: React.KeyboardEvent, it: BoardItemDto) => {
    const step = e.shiftKey ? 50 : 10;
    const move: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(it.id);
    } else if (move[e.key]) {
      e.preventDefault();
      const [dx, dy] = move[e.key]!;
      void save(it.id, { x: Math.max(LEAST, it.x + dx), y: Math.max(LEAST, it.y + dy) });
    } else if (e.key === 'Escape') setSelected(null);
  };

  const lower = [...ZOOMS].reverse().find((z) => z < zoom - 0.001);
  const higher = ZOOMS.find((z) => z > zoom + 0.001);
  const side = ORIGIN + width;
  const tall = ORIGIN + height;

  return (
    <div className={full ? 'flex min-h-0 flex-1 flex-col' : 'mt-6'}>
      <div className={`mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 ${full ? 'px-4' : ''}`}>
        <p className="meta">
          {items.length ? '' : 'Empty so far: paste, drop or add the first picture.'}
        </p>
        <div className="flex items-center gap-1" role="group" aria-label="Zoom">
          <Button tone="secondary" className="!min-h-9 !px-3" disabled={lower === undefined} onClick={() => lower !== undefined && paper.zoomTo(lower)} aria-label="Zoom out">
            −
          </Button>
          <button type="button" className="num min-h-9 w-16 text-sm text-ink-2 hover:text-ink" onClick={() => paper.zoomTo(1)} aria-label="Full size">
            {Math.round(zoom * 100)}%
          </button>
          <Button tone="secondary" className="!min-h-9 !px-3" disabled={higher === undefined} onClick={() => higher !== undefined && paper.zoomTo(higher)} aria-label="Zoom in">
            +
          </Button>
          <Button tone="quiet" className="ml-3 !text-ink" onClick={() => onFull(!full)}>
            {full ? 'Leave full screen' : 'Full screen'}
          </Button>
        </div>
      </div>
      <div
        ref={scroller}
        className={`relative cursor-grab overflow-auto overscroll-contain bg-paper active:cursor-grabbing ${NO_SCROLLBARS} ${
          full ? `min-h-0 flex-1 ${dropping ? 'outline outline-2 -outline-offset-2 outline-ink' : ''}` : `h-[72vh] min-h-[30rem] rounded-[2px] border ${dropping ? 'border-ink' : 'border-rule-strong'}`
        }`}
        style={{
          // Drafting paper, a faint dot every 32 units, over all of it whatever the zoom: no edge to it.
          backgroundImage: 'radial-gradient(circle, var(--color-rule-strong) 0.9px, transparent 1.1px)',
          backgroundSize: `${32 * zoom}px ${32 * zoom}px`,
          backgroundAttachment: 'local',
        }}
        {...paper.bind}
        onMouseDown={arm}
        onScroll={() => (pasteAt.current ? release() : undefined)}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          if (!dropping) setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropping(false);
          void post.transfer(e.dataTransfer, toBoard(e.clientX, e.clientY));
        }}
      >
        <div ref={sizer} style={{ width: side * zoom, height: tall * zoom }} className="relative">
          <div
            ref={world}
            className="absolute left-0 top-0 origin-top-left"
            style={{ width: side, height: tall, transform: `scale(${zoom})` }}
            onPointerMove={follow}
            onPointerUp={end}
            onPointerCancel={end}
          >
            <div className="absolute" style={{ left: ORIGIN, top: ORIGIN }}>
            {shown.map((it) => (
              <Item
                key={it.id}
                projectId={projectId}
                item={it}
                selected={selected === it.id}
                editing={editing === it.id}
                zoom={zoom}
                onBegin={begin}
                onKey={nudge}
                onRemove={() => void remove(it.id)}
                onEdit={() => (it.removable ? setEditing(it.id) : undefined)}
                onWords={(caption) => {
                  setEditing(null);
                  if (caption !== it.caption) void save(it.id, { caption });
                }}
              />
            ))}
            </div>
          </div>
        </div>
      </div>
      {sel ? <Selected projectId={projectId} item={sel} onRemove={() => void remove(sel.id)} onClose={() => setSelected(null)} /> : null}
      <ErrorLine error={err} />
      {/* The unseen box a right-click puts under the pointer, so the browser's menu offers Paste. It steps aside once the menu is up and keeps the focus for Paste. */}
      <textarea
        ref={catcher}
        aria-hidden="true"
        tabIndex={-1}
        className="fixed left-[-9999px] top-0 z-[70] h-6 w-6 resize-none overflow-hidden opacity-0"
        onContextMenu={() => window.setTimeout(() => put(-9999, 0), 50)}
        onPaste={(e) => {
          e.preventDefault();
          const where = pasteAt.current ?? middle();
          release();
          void post.transfer(e.clipboardData, where);
        }}
        onKeyDown={(e) => (e.ctrlKey || e.metaKey ? undefined : release())}
        onBlur={() => {
          pasteAt.current = null;
          put(-9999, 0);
        }}
      />
    </div>
  );
}

function Item({
  projectId,
  item: it,
  selected,
  editing,
  zoom,
  onBegin,
  onKey,
  onRemove,
  onEdit,
  onWords,
}: {
  projectId: string;
  item: BoardItemDto;
  selected: boolean;
  editing: boolean;
  zoom: number;
  onBegin: (e: React.PointerEvent, it: BoardItemDto, mode: 'move' | 'size') => void;
  onKey: (e: React.KeyboardEvent, it: BoardItemDto) => void;
  onRemove: () => void;
  onEdit: () => void;
  onWords: (caption: string) => void;
}) {
  const [words, setWords] = useState(it.caption ?? '');
  const [confirming, setConfirming] = useState(false);
  // The handles stay the same size on screen whatever the zoom: a 44px target round a 28px mark (guide §20.13).
  const steady = (origin: string) => ({ transform: `scale(${1 / zoom})`, transformOrigin: origin });
  const shown = selected || confirming ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100';
  const label = it.kind === 'note' ? `Note: ${it.caption}` : `${it.asset?.name ?? 'Link'}${it.caption ? `: ${it.caption}` : ''}, from ${it.by}`;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={selected}
      title={it.kind === 'note' ? undefined : (it.caption ?? it.asset?.name ?? undefined)}
      className={`group absolute touch-none select-none outline-none ${selected ? 'ring-2 ring-ink ring-offset-2 ring-offset-paper' : 'focus-visible:ring-2 focus-visible:ring-slate'} ${editing ? '' : 'cursor-grab active:cursor-grabbing'}`}
      style={{ left: it.x, top: it.y, width: it.w, zIndex: it.z }}
      onPointerDown={(e) => onBegin(e, it, 'move')}
      onKeyDown={(e) => (editing ? undefined : onKey(e, it))}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (it.kind === 'note') onEdit();
      }}
    >
      {it.kind === 'image' && it.asset?.thumbUrl && isPageAddress(it.asset.sourceUrl) ? (
        // A picture brought from a page: on a scrap, with the page's title and site under it.
        <div className={`${SCRAP} p-2 pb-3`} style={{ transform: `rotate(${tiltOf(it.id)}deg)` }}>
          <img src={it.asset.thumbUrl} alt={it.caption ?? it.asset.name} draggable={false} className="pointer-events-none block w-full bg-inset" />
          <span className="mt-2 line-clamp-2 block text-[0.875rem] leading-snug">{it.asset.name}</span>
          <span className="label mt-1 block">{siteOf(it.asset.sourceUrl)}</span>
        </div>
      ) : it.kind === 'image' && it.asset?.thumbUrl ? (
        <img src={it.asset.thumbUrl} alt={it.caption ?? it.asset.name} draggable={false} className="pointer-events-none block w-full rounded-[2px] bg-inset shadow-[0_1px_3px_rgba(32,35,31,0.18)]" />
      ) : it.kind === 'note' ? (
        editing ? (
          <textarea
            autoFocus
            className={`${SCRAP} w-full resize-none px-3.5 py-3 text-[0.9375rem] leading-snug outline-none`}
            rows={3}
            value={words}
            onPointerDown={(e) => e.stopPropagation()}
            onChange={(e) => setWords(e.target.value)}
            onBlur={() => (words.trim() ? onWords(words.trim()) : onWords(it.caption ?? ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              } else if (e.key === 'Escape') {
                setWords(it.caption ?? '');
                onWords(it.caption ?? '');
              }
            }}
          />
        ) : (
          <p className={`${SCRAP} whitespace-pre-line px-3.5 pb-3.5 pt-3 text-[0.9375rem] leading-snug`} style={{ transform: `rotate(${tiltOf(it.id)}deg)` }}>
            {it.caption}
          </p>
        )
      ) : (
        // A page with no picture: its title and site on the same scrap.
        <div className={`${SCRAP} p-3`} style={{ transform: `rotate(${tiltOf(it.id)}deg)` }}>
          <span className="line-clamp-2 block text-[0.9375rem] leading-snug">{it.asset?.name}</span>
          <span className="label mt-1 block">{host(it.asset?.url ?? null) || 'Link'}</span>
        </div>
      )}
      {it.kind !== 'note' && it.asset?.threads ? (
        <Link
          to={`/p/${projectId}/reviews/files/${it.asset.id}?from=board`}
          onPointerDown={(e) => e.stopPropagation()}
          className="label mt-1 inline-block bg-paper/90 px-1 hover:!text-ink"
        >
          {it.asset.threads} comment{it.asset.threads === 1 ? '' : 's'}
          {it.asset.openThreads ? `, ${it.asset.openThreads} open` : ''}
        </Link>
      ) : null}
      {/* Take it off: a trash can at the top-left corner, across from the size handle so the two never meet on a short note; asked once before it goes. */}
      {it.removable && !editing ? (
        <div className={`absolute -left-3 -top-3 transition-opacity duration-[120ms] ${shown}`} style={steady('top left')}>
          {confirming ? (
            <span className="flex items-center gap-2 rounded-[3px] border border-ink/40 bg-paper px-2 py-1 text-[0.8125rem] shadow-[0_1px_3px_rgba(32,35,31,0.2)]" onPointerDown={(e) => e.stopPropagation()}>
              Take it off?
              <button type="button" className="link min-h-9 text-alert" onClick={onRemove}>
                Yes
              </button>
              <button type="button" className="link min-h-9 text-ink-2" onClick={() => setConfirming(false)}>
                No
              </button>
            </span>
          ) : (
            <button
              type="button"
              aria-label="Take it off the board"
              title="Take it off the board"
              className="flex h-11 w-11 items-center justify-center"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setConfirming(true)}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full border border-ink/40 bg-paper text-ink-2 shadow-[0_1px_3px_rgba(32,35,31,0.2)] hover:text-alert">
                <TrashIcon />
              </span>
            </button>
          )}
        </div>
      ) : null}
      {/* Size it: the bottom-right corner to drag, its arrow running in toward the middle and out through the corner. */}
      {!editing ? (
        <span
          role="img"
          aria-label="Drag to resize"
          title="Drag to resize"
          className={`absolute -bottom-3 -right-3 flex h-11 w-11 cursor-nwse-resize items-center justify-center transition-opacity duration-[120ms] ${shown}`}
          style={steady('bottom right')}
          onPointerDown={(e) => onBegin(e, it, 'size')}
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full border border-ink/40 bg-paper text-ink-2 shadow-[0_1px_3px_rgba(32,35,31,0.2)]">
            <ResizeIcon />
          </span>
        </span>
      ) : null}
    </div>
  );
}

/** What the selected thing is, who put it there, and what can be done with it. */
function Selected({ projectId, item: it, onRemove, onClose }: { projectId: string; item: BoardItemDto; onRemove: () => void; onClose: () => void }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-rule pb-3">
      <div className="min-w-0">
        <p className="min-w-0 break-words">
          {it.kind === 'note' ? `“${it.caption}”` : it.asset?.name}
          {it.kind !== 'note' && it.caption ? <span className="text-ink-2"> · {it.caption}</span> : null}
        </p>
        <p className="meta">
          Put here by {it.by}, <span className="num">{day(it.at)}</span>
          {it.asset?.sourceUrl ? (
            <>
              {' · '}
              <a href={it.asset.sourceUrl} target="_blank" rel="noreferrer" className="link">
                From {host(it.asset.sourceUrl)}
              </a>
            </>
          ) : null}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 text-sm">
        {it.asset ? (
          <Link to={`/p/${projectId}/reviews/files/${it.asset.id}?from=board`} className="link min-h-11 py-2.5 text-ink-2 hover:text-ink">
            {it.kind === 'image' ? 'Open: mark it up, talk about it' : 'Talk about it'}
          </Link>
        ) : null}
        {it.kind === 'link' && it.asset?.url ? (
          <a href={it.asset.url} target="_blank" rel="noreferrer" className="link min-h-11 py-2.5 text-ink-2 hover:text-ink">
            Open the link
          </a>
        ) : null}
        {it.removable ? (
          confirming ? (
            <span className="flex items-center gap-3">
              Take it off the board?
              <Button tone="danger" onClick={onRemove}>
                Yes
              </Button>
              <Button tone="quiet" onClick={() => setConfirming(false)}>
                No
              </Button>
            </span>
          ) : (
            <Button tone="quiet" onClick={() => setConfirming(true)}>
              Take it off the board
            </Button>
          )
        ) : null}
        <Button tone="quiet" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/** On a phone: the board top to bottom, as it is laid out; arranging it waits for a wider screen. */
function Stack({ projectId, items }: { projectId: string; items: BoardItemDto[] }) {
  const [err, setErr] = useState<string | null>(null);
  // Asked once before it goes, as on the canvas.
  const [confirming, setConfirming] = useState<string | null>(null);
  const order = [...items].sort((a, b) => Math.round(a.y / 120) - Math.round(b.y / 120) || a.x - b.x);
  // Opened from a line of the timeline (P-26), it reads from that thing, marked as chosen is on the canvas.
  const wanted = useWanted(items);
  const wantedRow = useRef<HTMLLIElement>(null);
  useEffect(() => {
    wantedRow.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
  }, [wanted]);
  const remove = async (id: string) => {
    try {
      queryClient.setQueryData(boardKey(projectId), await api<BoardDto>(`/projects/${projectId}/board/items/${id}`, { method: 'DELETE' }));
      // What came off may no longer be shared: the project's files say so.
      void queryClient.invalidateQueries({ queryKey: assetsKey(projectId) });
      setConfirming(null);
    } catch (e) {
      setErr(errorText(e));
    }
  };
  if (!items.length) return <p className="mt-8 text-ink-3">Empty so far: add the first picture, or paste one.</p>;
  return (
    <div className="mt-8">
      <p className="meta mb-4">Arrange the board on a wider screen; here it reads top to bottom.</p>
      <ol className="space-y-8">
        {order.map((it) => (
          <li key={it.id} ref={it.id === wanted?.id ? wantedRow : undefined} className={it.id === wanted?.id ? 'ring-2 ring-ink ring-offset-4 ring-offset-paper' : undefined}>
            {it.kind === 'note' ? (
              <h2 className="border-b border-ink/40 pb-1 text-[1.375rem]">{it.caption}</h2>
            ) : (
              <>
                {it.asset?.thumbUrl ? (
                  <Link to={`/p/${projectId}/reviews/files/${it.asset.id}?from=board`}>
                    <img src={it.asset.thumbUrl} alt={it.caption ?? it.asset.name} loading="lazy" className="block w-full rounded-[2px] bg-inset" />
                  </Link>
                ) : (
                  <a href={it.asset?.url ?? '#'} target="_blank" rel="noreferrer" className="block rounded-[2px] border border-rule-strong bg-sheet p-3">
                    <span className="label">{host(it.asset?.url ?? null) || 'Link'}</span>
                    <span className="mt-1 block">{it.asset?.name}</span>
                  </a>
                )}
                {it.caption ? <p className="mt-1.5 text-ink-2">{it.caption}</p> : null}
                <p className="meta mt-1 flex flex-wrap gap-x-4">
                  <span>
                    {it.by}, <span className="num">{day(it.at)}</span>
                  </span>
                  {it.asset ? (
                    <Link to={`/p/${projectId}/reviews/files/${it.asset.id}?from=board`} className="link">
                      {it.asset.threads ? `${it.asset.threads} comment${it.asset.threads === 1 ? '' : 's'}` : 'Talk about it'}
                    </Link>
                  ) : null}
                  {it.removable ? (
                    confirming === it.id ? (
                      <span className="flex flex-wrap items-center gap-x-3">
                        Take it off the board?
                        <button type="button" className="link min-h-11 text-alert" onClick={() => void remove(it.id)}>
                          Yes
                        </button>
                        <button type="button" className="link min-h-11 text-ink-2" onClick={() => setConfirming(null)}>
                          No
                        </button>
                      </span>
                    ) : (
                      <button type="button" className="link min-h-11 text-ink-3 hover:text-alert" onClick={() => setConfirming(it.id)}>
                        Take it off
                      </button>
                    )
                  ) : null}
                </p>
              </>
            )}
          </li>
        ))}
      </ol>
      <ErrorLine error={err} />
    </div>
  );
}

/** A trash can, drawn: lid, handle, can and its ribs. */
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 4h11M6 4V2.6h4V4M3.8 4l.8 9.4h6.8l.8-9.4M6.6 6.5v4.6M9.4 6.5v4.6" />
    </svg>
  );
}

/** One arrow on the diagonal through the corner, a head at each end: in toward the middle to shrink, out to grow. */
function ResizeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 3.5l9 9M3.5 8V3.5H8M12.5 8v4.5H8" />
    </svg>
  );
}
